import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Gauge from 'components/visualize/recharts/gauge/Gauge';
import GaugeTooltip from 'components/visualize/recharts/gauge/GaugeTooltip';
import BarLineChart from 'components/visualize/recharts/composed-charts/BarLineChart';
import TimeGap from 'pages/profile/resource/components/time-gap/TimeGap';
import {
  getNetSentimentScore,
  getNetSentimentScoreTrend,
  getNssApiPath,
  resetNetSentimentScore,
  NSS_GAUGE_RANGE,
  getNssGaugeColors,
  formatNssGaugeValue,
  getNssGaugeLabels,
} from 'store';
import {
  commaSeperator,
  getRequestCacheAdaptedDate,
  getSettingsDefaultLanguage,
  magicNumbers,
  sentimentColors,
} from 'utils';
import { StyledNetSentimentScore } from './NetSentimentScore.styles';

const TREND_AXIS_DOMAINS = { left: [0, 100], right: [-100, 100] };
/** The percentage axis always shows this fixed scale, regardless of the data's own range. */
const TREND_AXIS_TICKS = { left: [0, 25, 50, 75, 100] };
/**
 * Hourly buckets say little about a sentiment trend, so the picker keeps the
 * option visible but never lets it be chosen; a sub-24h range shows one daily bar.
 */
const NSS_DISABLED_TIME_GAPS = ['hour'];

const NetSentimentScore = ({
  platform,
  queryId,
  filters = {},
  variant = 'statistics',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isResource = variant === 'resource';
  const scope = isResource ? 'resourceProfile' : 'statistics';
  const apiPath = getNssApiPath(platform);

  const currentQuery = useSelector((state) => state.pageReducer.currentQuery);
  const currentDate = useSelector((state) => state.pageReducer.currentDate);
  const onApplySelectedFilters = useSelector(
    (state) => state.filterReducer.onApplySelectedFilters
  );
  const defaultLanguageSelectBox = useSelector(
    (state) => state.settingsReducer.filters.public.defaultLanguageSelectBox
  );
  const timeGap = useSelector(
    (state) => state.chartReducer?.[scope]?.[platform]?.nssTrend
  );
  const index = useSelector(
    (state) => state.netSentimentScoreReducer[scope].index.data
  );
  const indexLoading = useSelector(
    (state) => state.netSentimentScoreReducer[scope].index.loading
  );
  const indexError = useSelector(
    (state) => state.netSentimentScoreReducer[scope].index.error
  );
  const trend = useSelector(
    (state) => state.netSentimentScoreReducer[scope].trend.data
  );
  const trendLoading = useSelector(
    (state) => state.netSentimentScoreReducer[scope].trend.loading
  );
  const trendError = useSelector(
    (state) => state.netSentimentScoreReducer[scope].trend.error
  );

  /**
   * The page rebuilds its filter object on every render, so the request is
   * assembled from a ref at call time instead of from a memo that would go
   * stale between filter changes. The statistics page reuses the cached
   * request windows its other charts use, the source profile does not.
   */
  const requestRef = useRef();
  requestRef.current = {
    params: {
      q: currentQuery.q,
      query: queryId || undefined,
      ...getRequestCacheAdaptedDate(currentDate, !isResource),
      langs: getSettingsDefaultLanguage(defaultLanguageSelectBox),
      ...filters,
    },
    timeGap,
  };

  const fetchIndex = useCallback(() => {
    if (!apiPath) return;
    dispatch(
      getNetSentimentScore({
        scope,
        apiPath,
        params: requestRef.current.params,
      })
    );
  }, [apiPath, dispatch, scope]);

  const fetchTrend = useCallback(() => {
    if (!apiPath) return;
    dispatch(
      getNetSentimentScoreTrend({
        scope,
        apiPath,
        params: requestRef.current.params,
        timeGap: requestRef.current.timeGap,
      })
    );
  }, [apiPath, dispatch, scope]);

  /**
   * The page bumps `onApplySelectedFilters` right after mount, so the fetch is
   * debounced the same way the surrounding charts are and only fires once.
   */
  const fetchTimeout = useRef(null);
  useEffect(() => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchIndex();
      fetchTrend();
    }, magicNumbers.CHART_FETCH_DEBOUNCE_DELAY);

    return () => clearTimeout(fetchTimeout.current);
  }, [fetchIndex, fetchTrend, onApplySelectedFilters]);

  useEffect(
    () => () => {
      dispatch(resetNetSentimentScore(scope));
    },
    [dispatch, scope]
  );

  const axisLabels = useMemo(
    () => ({
      yAxis: {
        left: t('statistics:netSentimentScore.percentageAxisLabel'),
        right: t('statistics:netSentimentScore.scoreAxisLabel'),
      },
    }),
    [t]
  );

  const gaugeLabels = useMemo(() => getNssGaugeLabels(), [t]);

  /** Right to left the design reads positive, neutral, negative. */
  const gaugeLegend = useMemo(() => {
    const distribution = index?.distribution;
    if (!distribution) return null;
    return [
      {
        key: 'positive',
        title: t('filter:positive'),
        color: sentimentColors.positive,
        value: t('statistics:netSentimentScore.percentage', {
          value: commaSeperator(distribution.positive),
        }),
      },
      {
        key: 'neutral',
        title: t('filter:neutral'),
        color: sentimentColors.neutral,
        value: t('statistics:netSentimentScore.percentage', {
          value: commaSeperator(distribution.neutral),
        }),
      },
      {
        key: 'negative',
        title: t('filter:negitive'),
        color: sentimentColors.negative,
        value: t('statistics:netSentimentScore.percentage', {
          value: commaSeperator(distribution.negative),
        }),
      },
    ];
  }, [index?.distribution, t]);

  const gaugeHoverTooltip = useMemo(() => {
    const distribution = index?.distribution;
    const hasCounts =
      distribution?.positiveCount != null &&
      distribution?.neutralCount != null &&
      distribution?.negativeCount != null;
    if (!hasCounts) return null;

    return (
      <GaugeTooltip
        rows={[
          {
            key: 'positive',
            value: commaSeperator(distribution.positiveCount),
            label: t('statistics:netSentimentScore.tooltip.positiveCount'),
          },
          {
            key: 'neutral',
            value: commaSeperator(distribution.neutralCount),
            label: t('statistics:netSentimentScore.tooltip.neutralCount'),
          },
          {
            key: 'negative',
            value: commaSeperator(distribution.negativeCount),
            label: t('statistics:netSentimentScore.tooltip.negativeCount'),
          },
        ]}
      />
    );
  }, [index?.distribution, t]);

  if (!apiPath) return null;

  return (
    <StyledNetSentimentScore className='net-sentiment-score flex'>
      {/*
       * RTL flex lays out children right-to-left, so the trend chart is
       * listed first in the DOM to land on the right with the gauge on the
       * left, matching the page's design.
       */}
      <BarLineChart
        className='nss-trend-card'
        title={t('statistics:netSentimentScore.trendTitle')}
        tooltip={t(
          isResource
            ? 'profile:netSentimentScore.trendTooltip'
            : 'statistics:netSentimentScore.trendTooltip'
        )}
        data={trend?.chartData}
        chartKeys={trend?.chartKeys}
        legends={trend?.legends}
        tooltipOrder={trend?.tooltipOrder}
        loading={trendLoading}
        error={trendError}
        refresh={fetchTrend}
        axisLabels={axisLabels}
        yAxisDomains={TREND_AXIS_DOMAINS}
        yAxisTicks={TREND_AXIS_TICKS}
        /**
         * `name` is only the visible label; the axis runs on the unique `time`
         * so a repeated label can never resolve the hover to the wrong bucket.
         */
        xAxisKey='time'
        actions={
          <TimeGap
            onSubmit={fetchTrend}
            page={scope}
            platform={platform}
            boxName='nssTrend'
            disabled={NSS_DISABLED_TIME_GAPS}
            defaultTimeGap='day'
          />
        }
      />
      <Gauge
        className='nss-gauge-card'
        title={t('statistics:netSentimentScore.title')}
        tooltip={t(
          isResource
            ? 'profile:netSentimentScore.tooltip'
            : 'statistics:netSentimentScore.tooltip'
        )}
        label={t('statistics:netSentimentScore.gaugeLabel')}
        value={index?.nss}
        min={NSS_GAUGE_RANGE.min}
        max={NSS_GAUGE_RANGE.max}
        colors={getNssGaugeColors()}
        formatValue={formatNssGaugeValue}
        minLabel={gaugeLabels.min}
        maxLabel={gaugeLabels.max}
        midLabel={gaugeLabels.mid}
        noData={index?.sampleSize === 0}
        loading={indexLoading}
        error={indexError}
        refresh={fetchIndex}
        legend={gaugeLegend}
        hoverTooltipContent={gaugeHoverTooltip}
        data-testid='nss-gauge'
      />
    </StyledNetSentimentScore>
  );
};

NetSentimentScore.propTypes = {
  platform: PropTypes.string.isRequired,
  queryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filters: PropTypes.object,
  variant: PropTypes.oneOf(['statistics', 'resource']),
};

export default NetSentimentScore;
